import React, { useState } from 'react';
import {
  ShieldCheck,
  Lock,
  FileText,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Building2,
  ArrowRight
} from 'lucide-react';
import { useGst } from '../context/GstContext';

interface LegalGateModalProps {
  isOpen: boolean;
  onAccept: () => void;
}

export const LegalGateModal: React.FC<LegalGateModalProps> = ({ isOpen, onAccept }) => {
  const { clientId, clientLegalName, user } = useGst();
  const [agreedNda, setAgreedNda] = useState(false);
  const [agreedDpa, setAgreedDpa] = useState(false);
  const [agreedZeroTraining, setAgreedZeroTraining] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const allChecked = agreedNda && agreedDpa && agreedZeroTraining;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allChecked) return;

    setSubmitting(true);
    try {
      // Store consent locally and trigger accept callback
      const consentRecord = {
        clientId,
        clientLegalName,
        acceptedBy: user?.email || 'Authorized User',
        acceptedAt: new Date().toISOString(),
        version: 'DPA-2026.1',
        ndaRef: 'M-NDA-VERVE-AI-2026'
      };
      localStorage.setItem(`gst_consent_${clientId}`, JSON.stringify(consentRecord));
      onAccept();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-cyan-500/40 bg-[#0e1015] text-[#f7f7f8] shadow-2xl p-6 sm:p-8 space-y-6">
        {/* Brand & Badge */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center text-cyan-400 shadow-lg shadow-cyan-500/20">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white tracking-tight">
                  Data Protection & Privacy Charter
                </h2>
                <span className="rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 px-2 py-0.5 text-[10px] font-mono font-bold uppercase">
                  Statutory Consent
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Client Organization: <strong className="text-white">{clientLegalName || 'Johalibash & Company'}</strong> &bull; <span className="font-mono text-cyan-300">{clientId || 'Tenant'}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Informational Box */}
        <div className="rounded-2xl border border-white/[0.08] bg-[#14161d] p-4 text-xs text-slate-300 space-y-2.5 leading-relaxed">
          <p>
            Before ingesting transactional documents into the <strong className="text-white">gst.verveai.co</strong> vault, Verve AI Labs requires confirmation of our binding Data Protection standards.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 font-mono text-[11px]">
            <div className="flex items-center gap-1.5 text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              <span>100% Client Data Ownership</span>
            </div>
            <div className="flex items-center gap-1.5 text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              <span>Zero Public AI Model Training</span>
            </div>
            <div className="flex items-center gap-1.5 text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              <span>AES-256 At-Rest Encryption</span>
            </div>
            <div className="flex items-center gap-1.5 text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              <span>1-Click Verified Data Purge</span>
            </div>
          </div>
        </div>

        {/* Required Acknowledgements */}
        <form onSubmit={handleSubmit} className="space-y-3.5">
          <label className="flex items-start gap-3 p-3 rounded-xl border border-white/[0.06] bg-[#12141a] hover:border-cyan-500/30 transition cursor-pointer">
            <input
              type="checkbox"
              checked={agreedDpa}
              onChange={(e) => setAgreedDpa(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-400"
            />
            <span className="text-xs text-slate-300 leading-snug">
              <strong className="text-white">Data Protection Addendum (DPA):</strong> I confirm that <strong className="text-cyan-300">{clientLegalName || 'Johalibash'}</strong> acts as the exclusive Data Controller and Verve AI Labs acts as Data Processor under a strict 36-hour breach notification guarantee.
            </span>
          </label>

          <label className="flex items-start gap-3 p-3 rounded-xl border border-white/[0.06] bg-[#12141a] hover:border-cyan-500/30 transition cursor-pointer">
            <input
              type="checkbox"
              checked={agreedZeroTraining}
              onChange={(e) => setAgreedZeroTraining(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-400"
            />
            <span className="text-xs text-slate-300 leading-snug">
              <strong className="text-white">Zero AI Training & In-Memory Extraction:</strong> I acknowledge that all multimodal invoice extractions occur via Google Cloud Vertex AI Enterprise SLAs with zero data retention, and no records are used to train foundational AI.
            </span>
          </label>

          <label className="flex items-start gap-3 p-3 rounded-xl border border-white/[0.06] bg-[#12141a] hover:border-cyan-500/30 transition cursor-pointer">
            <input
              type="checkbox"
              checked={agreedNda}
              onChange={(e) => setAgreedNda(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-400"
            />
            <span className="text-xs text-slate-300 leading-snug">
              <strong className="text-white">Mutual Confidentiality & Non-Circumvention:</strong> I acknowledge the terms of the Master Mutual NDA ensuring vendor details and financial transaction records cannot be used to circumvent our commercial relationships.
            </span>
          </label>

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="submit"
              disabled={!allChecked || submitting}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-bold transition ${
                allChecked
                  ? 'bg-cyan-500 hover:bg-cyan-400 text-black shadow-lg shadow-cyan-500/25'
                  : 'bg-white/10 text-slate-500 cursor-not-allowed'
              }`}
            >
              <span>Authorize Data Vault & Continue to Ingestion</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
